import { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { UNITS } from "@/data/units";
import { whatsappUrl } from "@/lib/links";
import { track } from "@/lib/tracking";

const EVENT = "nook:open-trial-dialog";

/** Abre o pop-up de seleção de unidade para o Day Use Nook. */
export function openTrialDialog(where = "site") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { where } }));
}

export function TrialDialog() {
  const [open, setOpen] = useState(false);
  const [where, setWhere] = useState("site");

  useEffect(() => {
    const onOpen = (e: Event) => {
      setWhere((e as CustomEvent<{ where?: string }>).detail?.where ?? "site");
      setOpen(true);
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Escolha a unidade para garantir seu Day Use"
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm p-0 sm:items-center sm:p-6"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-rise relative w-full max-w-lg overflow-hidden rounded-t-lg border border-border bg-background p-6 sm:rounded-sm sm:p-8"
      >
        {/* Grafismo estilo graffiti — minimalista, com opacidade */}
        <svg
          aria-hidden="true"
          viewBox="0 0 400 320"
          preserveAspectRatio="xMidYMid slice"
          className="pointer-events-none absolute inset-0 h-full w-full text-nook-green"
        >
          <defs>
            <filter id="spray" x="-20%" y="-20%" width="140%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
            </filter>
          </defs>
          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" filter="url(#spray)">
            {/* Tag fluida superior */}
            <path d="M-30 60 C 20 20, 80 100, 140 60 S 240 10, 320 50 S 420 20, 460 70" strokeWidth="18" opacity="0.10" />
            <path d="M-20 75 C 30 35, 90 115, 150 75 S 250 25, 330 65 S 430 35, 470 85" strokeWidth="6" opacity="0.14" />

            {/* Drips verticais */}
            <path d="M60 0 L 60 120 M60 120 L 52 145 M60 120 L 68 138" strokeWidth="5" opacity="0.12" />
            <path d="M340 0 L 340 90 M340 90 L 332 118 M340 90 L 348 110" strokeWidth="4" opacity="0.10" />

            {/* Tag diagonal */}
            <path d="M-20 280 C 60 240, 120 300, 200 260 S 320 220, 420 260" strokeWidth="14" opacity="0.09" />

            {/* Smiley estilo graffiti */}
            <g transform="translate(280, 210) scale(0.7)" opacity="0.12">
              <circle cx="0" cy="0" r="45" strokeWidth="4" />
              <path d="M-22 -8 Q -15 -18, -8 -8" strokeWidth="3" />
              <path d="M8 -8 Q 15 -18, 22 -8" strokeWidth="3" />
              <path d="M-22 12 Q 0 32, 22 12" strokeWidth="3" />
            </g>

            {/* Splat de tinta */}
            <path d="M40 240 Q 30 220, 50 215 Q 70 210, 80 230 Q 90 250, 70 260 Q 50 270, 35 255 Z" strokeWidth="2" opacity="0.08" />

            {/* Linhas soltas */}
            <path d="M120 0 L 140 80" strokeWidth="3" opacity="0.10" />
            <path d="M380 160 L 400 240" strokeWidth="3" opacity="0.10" />
          </g>
        </svg>

        <div className="relative">
        <div className="flex items-start justify-between gap-6">

          <div>
            <span className="eyebrow">Day Use Nook · R$ 100</span>
            <h2 className="display mt-3 text-3xl leading-[0.95]">
              Escolha sua
              <br />
              unidade Nook.
            </h2>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Você será direcionado ao WhatsApp da unidade para agendar. O valor é abatido da mensalidade ao iniciar um plano.
        </p>

        <ul className="mt-6 space-y-3">
          {UNITS.map((u) => (
            <li key={u.slug}>
              <a
                href={whatsappUrl(u)}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  track("click_whatsapp", { where, unit: u.slug, source: "trial_dialog" });
                  setOpen(false);
                }}
                className="group flex items-center justify-between gap-4 rounded-sm border border-border bg-card/80 p-4 backdrop-blur-sm transition-colors hover:border-nook-green hover:bg-card"
              >
                <span>
                  <span className="block text-sm font-semibold text-foreground">{u.shortName}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{u.addressLine}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-nook-green transition-transform group-hover:translate-x-1" />
              </a>
            </li>
          ))}
        </ul>
        </div>
      </div>
    </div>
  );
}

